/**
 * Enforces nw-bundle-main cart rules after Upcart / cart drawer edits:
 * - free_pouch total qty cannot exceed main_set (gift set) total qty
 * - each free_pouch line is capped at qty 1 (split lines at add time; clamp legacy carts)
 */
(function () {
  'use strict';

  const PROP_MATCHA_BUNDLE = '_matcha_set_bundle';
  const PROP_ROLE = '_nw_bundle_role';
  const ROLE_MAIN = 'main_set';
  const ROLE_FREE_POUCH = 'free_pouch';

  let debounceTimer = null;
  let guardRunning = false;
  /** @type {object | null} */
  let pendingCartSnapshot = null;

  function getCartBasePath() {
    if (typeof window.KROWN !== 'undefined' && KROWN.settings?.routes?.cart_url) {
      return String(KROWN.settings.routes.cart_url).replace(/\/$/, '');
    }
    return '/cart';
  }

  function getCartJsUrl() {
    return `${getCartBasePath()}.js`;
  }

  function getCartChangeJsUrl() {
    return `${getCartBasePath()}/change.js`;
  }

  function propTruthy(val) {
    if (val == null) return false;
    const s = String(val).trim().toLowerCase();
    return s !== '' && s !== 'false' && s !== '0';
  }

  function lineItemPropsMap(item) {
    if (!item || item.properties == null) return {};
    const p = item.properties;
    if (Array.isArray(p)) {
      const out = {};
      p.forEach((kv) => {
        if (kv != null && Object.prototype.hasOwnProperty.call(kv, 'name')) {
          const n = kv.name != null ? String(kv.name) : '';
          if (n) out[n] = kv.value != null ? String(kv.value) : '';
        }
      });
      return out;
    }
    if (typeof p === 'object') return p;
    return {};
  }

  function lineNumberForCartItem(item, arrayIndexZeroBased) {
    if (item != null && item.line != null && item.line !== '') {
      const ln = parseInt(String(item.line), 10);
      if (Number.isFinite(ln) && ln >= 1) return ln;
    }
    return arrayIndexZeroBased + 1;
  }

  function isMatchaBundleLine(item) {
    return propTruthy(lineItemPropsMap(item)[PROP_MATCHA_BUNDLE]);
  }

  function bundleRole(item) {
    const role = lineItemPropsMap(item)[PROP_ROLE];
    return role != null ? String(role).trim() : '';
  }

  /** Supports legacy bundle lines that only have `_matcha_set_bundle` (no `_nw_bundle_role`). */
  function bundleLineEffectiveRole(item) {
    const role = bundleRole(item);
    if (role) return role;
    if (!isMatchaBundleLine(item)) return '';
    const price = parseInt(String(item.final_line_price ?? item.price ?? item.line_price ?? 0), 10);
    if (price === 0) return ROLE_FREE_POUCH;
    return ROLE_MAIN;
  }

  /** @returns {Array<{ line: number, quantity: number }>} */
  function buildCartCorrections(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.some(isMatchaBundleLine)) return [];

    let mainQty = 0;
    for (const item of items) {
      if (!isMatchaBundleLine(item) || bundleLineEffectiveRole(item) !== ROLE_MAIN) continue;
      mainQty += Math.max(0, parseInt(String(item.quantity), 10) || 0);
    }

    /** @type {Array<{ line: number, targetQty: number, originalQty: number }>} */
    const pouchRows = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!isMatchaBundleLine(item) || bundleLineEffectiveRole(item) !== ROLE_FREE_POUCH) continue;
      const originalQty = Math.max(0, parseInt(String(item.quantity), 10) || 0);
      const targetQty = Math.min(originalQty, 1);
      pouchRows.push({
        line: lineNumberForCartItem(item, i),
        targetQty,
        originalQty,
      });
    }

    let pouchTotal = pouchRows.reduce((sum, row) => sum + row.targetQty, 0);
    const allowedPouch = Math.max(0, mainQty);
    for (let i = pouchRows.length - 1; i >= 0 && pouchTotal > allowedPouch; i -= 1) {
      const row = pouchRows[i];
      const excess = pouchTotal - allowedPouch;
      const remove = Math.min(excess, row.targetQty);
      row.targetQty -= remove;
      pouchTotal -= remove;
    }

    /** @type {Array<{ line: number, quantity: number }>} */
    const changes = [];
    for (const row of pouchRows) {
      if (row.targetQty !== row.originalQty) {
        changes.push({ line: row.line, quantity: row.targetQty });
      }
    }
    return changes;
  }

  function applyCartChange(line, quantity) {
    return fetch(getCartChangeJsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ line, quantity }),
    }).then((response) => response.json());
  }

  function getUpcartDomRoot() {
    if (typeof window.upcartDocumentOrShadowRoot !== 'undefined' && window.upcartDocumentOrShadowRoot) {
      return window.upcartDocumentOrShadowRoot;
    }
    return document;
  }

  function variantIdFromCartItem(item) {
    if (!item) return null;
    const raw = item.variant_id ?? item.variantId ?? item.id;
    if (raw == null || raw === '') return null;
    return String(raw);
  }

  function hideUpcartQtyControls(root, variantId) {
    if (!root || !variantId) return;
    const id = String(variantId);
    const selectors = [
      `.upcart-public-cart-items [data-variant-id="${id}"] .upcart-public-component-product-tile__quantity-selector`,
      `.upcart-product-item[id*="${id}"] [class*="styles_QuantityField__"]`,
      `.upcart-product-item[id*="${id}"] [class*="QuantityField"]`,
      `[data-variant-id="${id}"] [class*="quantity-selector"]`,
      `[data-variant-id="${id}"] [class*="QuantityField"]`,
    ];
    for (const selector of selectors) {
      try {
        root.querySelectorAll(selector).forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('data-nw-bundle-qty-locked', 'true');
        });
      } catch (_) {
        /* no-op */
      }
    }
  }

  /** Hide Upcart +/- controls for bundle free-pouch lines (visual lock; guard still enforces server-side). */
  function lockFreePouchQtyInUpcart(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return;
    const root = getUpcartDomRoot();
    for (const item of items) {
      if (!isMatchaBundleLine(item) || bundleLineEffectiveRole(item) !== ROLE_FREE_POUCH) continue;
      const vid = variantIdFromCartItem(item);
      if (vid) hideUpcartQtyControls(root, vid);
    }
  }

  function scheduleUpcartQtyLock(cart) {
    const run = () => lockFreePouchQtyInUpcart(cart);
    window.requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 120);
    });
  }

  function refreshUpcartDrawer() {
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function syncUpcartAfterGuardCorrection() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    refreshUpcartDrawer();
    window.setTimeout(() => {
      try {
        const cart =
          typeof window.upcartGetCart === 'function' ? window.upcartGetCart() : null;
        if (cart) scheduleUpcartQtyLock(cart);
      } catch (_) {
        /* no-op */
      }
    }, 350);
  }

  /**
   * @param {object} [cartSnapshot] — Upcart passes cart on `upcartSubscribeCartUpdated`; otherwise fetch `/cart.js`.
   */
  function runBundleCartGuard(cartSnapshot) {
    if (guardRunning) return Promise.resolve();
    guardRunning = true;

    const loadCart =
      cartSnapshot && Array.isArray(cartSnapshot.items)
        ? Promise.resolve(cartSnapshot)
        : fetch(getCartJsUrl(), {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          }).then((r) => r.json());

    return loadCart
      .then((cart) => {
        const changes = buildCartCorrections(cart);
        if (!changes.length) return false;
        return changes
          .reduce((chain, ch) => chain.then(() => applyCartChange(ch.line, ch.quantity)), Promise.resolve())
          .then(() => true);
      })
      .then((didChange) => {
        if (didChange) {
          syncUpcartAfterGuardCorrection();
        }
      })
      .catch(() => {
        /* no-op */
      })
      .finally(() => {
        guardRunning = false;
      });
  }

  /**
   * @param {object} [cartSnapshot]
   */
  function scheduleBundleCartGuard(cartSnapshot) {
    if (cartSnapshot && Array.isArray(cartSnapshot.items)) {
      pendingCartSnapshot = cartSnapshot;
    }
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      const cart = pendingCartSnapshot;
      pendingCartSnapshot = null;
      runBundleCartGuard(cart || undefined);
    }, 450);
  }

  window.nwScheduleBundleCartGuard = scheduleBundleCartGuard;

  document.addEventListener('cart:refresh', () => scheduleBundleCartGuard());
  document.addEventListener('add-to-cart', () => scheduleBundleCartGuard());

  if (typeof window.fetch === 'function') {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = function nwBundleGuardedFetch(input, init) {
      const result = nativeFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : input?.url;
        if (url && /\/cart\/(add|change|update)(\.js)?/i.test(String(url))) {
          result.then(() => scheduleBundleCartGuard()).catch(() => {
            /* no-op */
          });
        }
      } catch (_) {
        /* no-op */
      }
      return result;
    };
  }

  /** Upcart app embed loads after theme scripts; poll until API is available. */
  function bindUpcartIntegrations() {
    if (typeof window.upcartSubscribeCartUpdated !== 'function') return false;

    window.upcartSubscribeCartUpdated(function (event) {
      scheduleBundleCartGuard(event?.cart);
      scheduleUpcartQtyLock(event?.cart);
    });

    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(function (event) {
        scheduleUpcartQtyLock(event?.cart);
      });
    }

    if (typeof window.upcartSubscribeCartOpened === 'function') {
      window.upcartSubscribeCartOpened(function () {
        window.setTimeout(() => {
          try {
            const cart =
              typeof window.upcartGetCart === 'function' ? window.upcartGetCart() : null;
            if (cart) scheduleUpcartQtyLock(cart);
          } catch (_) {
            /* no-op */
          }
        }, 150);
      });
    }

    return true;
  }

  let upcartBindAttempts = 0;
  const UPCART_BIND_MAX_ATTEMPTS = 80;

  function tryBindUpcart() {
    if (bindUpcartIntegrations()) return;
    upcartBindAttempts += 1;
    if (upcartBindAttempts < UPCART_BIND_MAX_ATTEMPTS) {
      window.setTimeout(tryBindUpcart, 250);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryBindUpcart);
  } else {
    tryBindUpcart();
  }
})();
